import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import {
  buildWatchlistSourceOptions,
  watchlistSourceLabel,
} from '../../constants/watchlistDefaults';
import useIsMobile from '../../hooks/useIsMobile';
import BottomSheet from './BottomSheet';
import FilterChip from './FilterChip';
import SheetOptionList from './SheetOptionList';

function DesktopControlPanel({ show, title, onHide, testId, closeLabel, align = 'right', children, colors }) {
  if (!show) return null;
  return (
    <div
      role="dialog"
      aria-label={title}
      data-testid={testId}
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: align === 'left' ? 0 : 'auto',
        right: align === 'left' ? 'auto' : 0,
        zIndex: 45,
        width: 380,
        maxWidth: 'calc(100vw - 48px)',
        maxHeight: 'min(70vh, 620px)',
        overflowY: 'auto',
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        backgroundColor: colors.bgPrimary,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 44,
          padding: '6px 8px 6px 14px',
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.bgPrimary,
        }}
      >
        <strong style={{ color: colors.text, fontSize: 14 }}>{title}</strong>
        <button
          type="button"
          aria-label={closeLabel}
          onClick={onHide}
          style={{
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            borderRadius: 8,
            background: 'transparent',
            color: colors.textMuted,
            cursor: 'pointer',
          }}
        >
          <X size={18} />
        </button>
      </div>
      <div style={{ padding: '12px 14px' }}>{children}</div>
    </div>
  );
}

/**
 * Shared Lists chip + panel used by Relative Strength and RS Study.
 * Render `chip` inside the toolbar row and `panel` as a sibling so overflow
 * on the chip bar does not clip the menu.
 */
export function useWatchlistSourceMenu({
  value,
  onChange,
  watchlists = [],
  options,
  testId = 'watchlist-source',
  sheetTestId,
  ariaLabel = 'Lists',
  align = 'right',
  fill = false,
  valueLabel,
  onBeforeOpen,
  tone = null,
} = {}) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const chipRef = useRef(null);
  const panelRef = useRef(null);

  const resolvedOptions = useMemo(
    () => (options?.length ? options : buildWatchlistSourceOptions(watchlists)),
    [options, watchlists],
  );
  const sourceLabel = useMemo(() => {
    const fromOptions = resolvedOptions.find((option) => !option.header && option.value === value);
    return fromOptions?.label || watchlistSourceLabel(value, watchlists);
  }, [resolvedOptions, value, watchlists]);

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (chipRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, isMobile]);

  const listContent = (
    <SheetOptionList
      options={resolvedOptions}
      value={value}
      onChange={(next) => {
        onChange?.(next);
        close();
      }}
      ariaLabel={ariaLabel}
    />
  );

  const chip = (
    <span ref={chipRef} style={{ display: fill ? 'flex' : 'inline-flex', width: fill ? '100%' : undefined }}>
      <FilterChip
        testId={testId}
        ariaLabel={ariaLabel}
        ariaExpanded={open}
        value={valueLabel || sourceLabel}
        fill={fill}
        tone={tone}
        active={open}
        onClick={() => {
          if (!open) onBeforeOpen?.();
          setOpen((current) => !current);
        }}
      />
    </span>
  );

  const panel = isMobile ? (
    <BottomSheet
      show={open}
      onHide={close}
      title="Lists"
      testId={sheetTestId || `${testId}-sheet`}
      closeLabel="Close lists"
    >
      {listContent}
    </BottomSheet>
  ) : (
    <div ref={panelRef}>
      <DesktopControlPanel
        show={open}
        title="Lists"
        onHide={close}
        align={align}
        testId={sheetTestId || `${testId}-sheet`}
        closeLabel="Close lists"
        colors={colors}
      >
        {listContent}
      </DesktopControlPanel>
    </div>
  );

  return { chip, panel, open, close };
}

export default useWatchlistSourceMenu;
